/* Cart read dedupe v4 — scoped Seel compatibility and mutation-safe cart reads.
 * Collapses repeated GET /cart.js + /cart.json reads (fetch AND XMLHttpRequest)
 * from theme/app scripts into one shared network request within a short window.
 * Cache invalidates the moment anything mutates the cart. Apps receive
 * identical, valid data.
 */
(function () {
  if (window.__cartDedupe) return;
  window.__cartDedupe = true;
  var TTL = 3000;
  var cache = {};
  var pendingMutations = 0;
  var READ = /^\/cart\.(js|json)$/;
  var MUT = /^\/cart\/(add|update|change|clear)\.js/;

  /* Seel uses this tagged request for its own insurance-quote lifecycle.
   * Leave only that request untouched; normal theme and app cart reads can
   * still share a fresh response. */
  function isSeelCartRead(u) {
    try {
      var x = new URL(u, location.origin);
      return x.origin === location.origin && x.pathname === '/cart.js' && x.searchParams.get('src') === 'seel';
    } catch (e) {
      return false;
    }
  }

  function samePath(u) {
    try {
      var x = new URL(u, location.origin);
      return x.origin === location.origin ? x.pathname + x.search : null;
    } catch (e) { return null; }
  }

  /* Ghost-line-item guard (Phase 2, 9 Jul 2026): app scripts (Seel Worry-Free)
   * mutate the cart without telling the theme — observed live: Seel silently
   * POSTs /cart/change removing its line item when a quote is invalid, leaving
   * the drawer rendering stale line indices; the next tap then hits the wrong
   * line and the drawer wedges until reload. After ANY cart mutation, schedule
   * a cart:refresh resync; the theme's own flows dispatch cart:refresh
   * themselves, which cancels the pending timer, so theme-initiated actions
   * cost nothing extra. Empty detail makes the listeners re-fetch fresh state. */
  var refreshTimer = null;
  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: {} })
        );
      } catch (e) {}
    }, 1200);
  }
  document.addEventListener('cart:refresh', function () {
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
  }, true);

  var of = window.fetch;

  function cachedRead(key, args, ctx) {
    if (pendingMutations > 0) return of.apply(ctx || window, args || [key]);
    key = '/cart.js'; /* /cart.js and /cart.json return identical cart JSON — share one entry */
    var e = cache[key];
    var now = Date.now();
    if (e && now - e.t < TTL) return e.q.then(function (res) { return res.clone(); });
    var q = of.apply(ctx || window, args || [key]).then(function (res) {
      if (!res.ok) delete cache[key];
      return res;
    }, function (err) { delete cache[key]; throw err; });
    cache[key] = { t: now, q: q };
    return q.then(function (res) { return res.clone(); });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = samePath(url);
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

    if (p && MUT.test(p)) {
      cache = {};
      pendingMutations += 1;
      var m = of.apply(this, arguments);
      m.then(function () {
        pendingMutations = Math.max(0, pendingMutations - 1);
        cache = {};
        scheduleRefresh();
      }, function () {
        pendingMutations = Math.max(0, pendingMutations - 1);
        cache = {};
      });
      return m;
    }
    if (isSeelCartRead(url)) return of.apply(this, arguments);
    if (p && method === 'GET' && READ.test(p.split('?')[0])) {
      return cachedRead(p.split('?')[0], arguments, this);
    }
    return of.apply(this, arguments);
  };

  /* XMLHttpRequest: mutations invalidate; GET cart reads are answered from the
   * same shared cache (routed through fetch), so XHR and fetch users dedupe
   * against each other. */
  var oOpen = XMLHttpRequest.prototype.open;
  var oSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (m, u) {
    this.__crdKey = null;
    this.__crdMutation = false;
    this.__crdSeelRead = isSeelCartRead(u);
    try {
      var p = samePath(u);
      if (p) {
        if (MUT.test(p)) {
          this.__crdMutation = true;
        } else if (!this.__crdSeelRead && String(m).toUpperCase() === 'GET' && READ.test(p.split('?')[0])) {
          this.__crdKey = p.split('?')[0];
        }
      }
    } catch (e) {}
    return oOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr.__crdMutation) {
      cache = {};
      pendingMutations += 1;
      xhr.addEventListener('loadend', function () {
        pendingMutations = Math.max(0, pendingMutations - 1);
        cache = {};
        scheduleRefresh();
      }, { once: true });
      return oSend.apply(this, arguments);
    }
    if (xhr.__crdSeelRead) return oSend.apply(this, arguments);
    var key = xhr.__crdKey;
    if (!key) return oSend.apply(this, arguments);
    try {
      cachedRead(key).then(function (res) { return res.text(); }).then(function (text) {
        var rt = xhr.responseType;
        var body = text;
        if (rt === 'json') { try { body = JSON.parse(text); } catch (e) { body = null; } }
        try {
          Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true });
          Object.defineProperty(xhr, 'status', { value: 200, configurable: true });
          Object.defineProperty(xhr, 'statusText', { value: 'OK', configurable: true });
          Object.defineProperty(xhr, 'response', { value: body, configurable: true });
          if (rt === '' || rt === 'text') {
            Object.defineProperty(xhr, 'responseText', { value: text, configurable: true });
          }
          xhr.getAllResponseHeaders = function () { return 'content-type: application/json\r\n'; };
          xhr.getResponseHeader = function (h) { return /content-type/i.test(h) ? 'application/json' : null; };
        } catch (e) {}
        ['readystatechange', 'load', 'loadend'].forEach(function (t) {
          try { xhr.dispatchEvent(new Event(t)); } catch (e) {}
        });
      }).catch(function () {
        try { oSend.apply(xhr, []); } catch (e) {}
      });
    } catch (e) {
      return oSend.apply(this, arguments);
    }
  };
})();
